import React, {useMemo} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {Panel} from 'reactflow';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {alpha, keyframes, useTheme} from '@mui/material/styles';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import {useTranslation} from 'react-i18next';
import {AutomationFlow, AutomationStatus, ExecutionStatus} from 'crqlar-react-common/dist/models/automation/automation';
import {formatDateDMYHM, formatDateRelative} from 'crqlar-react-common/dist/utils/date.util';

import {EXECUTION_STATUS_LABEL_KEYS} from '../../../common/uiElements/ExecutionStatusElement';
import {isTestRunExecution, stoppedAtText} from '../executions/executionFormat.util';
import {FlowActivity} from '../hooks/useFlowActivity';
import {NextScheduledRun} from '../utils/nextScheduledRun';

interface FlowActivityPanelProps {
    flow?: AutomationFlow;
    activity?: FlowActivity;
    /** The scheduled trigger's next run, already computed; absent for a flow without one. */
    nextRun?: NextScheduledRun | null;
    executionPath: (executionId: string) => string;
    executionsPath: string;
}

const toDate = (value: Date | string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

interface ActivityRowProps {
    icon: React.ReactNode;
    text: React.ReactNode;
    detail?: string;
    tooltip?: string;
    to?: string;
    error?: boolean;
}

const ActivityRow: React.FC<ActivityRowProps> = ({icon, text, detail, tooltip, to, error}) => {
    const line = (
        <Typography variant={'body1'} color={error ? 'error.main' : 'text.primary'} sx={{lineHeight: 1.4}}>
            {text}
            {detail && (
                <Box component={'span'} sx={{color: error ? 'error.main' : 'text.secondary'}}> · {detail}</Box>
            )}
        </Typography>
    );

    const row = (
        <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{minWidth: 0}}>
            <Box sx={{
                display: 'flex',
                color: error ? 'error.main' : 'text.secondary',
                flex: '0 0 auto',
                '& > svg': {fontSize: 20}
            }}>
                {icon}
            </Box>
            <Box sx={{minWidth: 0}}>
                {to ? (
                    <Link component={RouterLink} to={to} underline={'hover'} color={'inherit'}>
                        {line}
                    </Link>
                ) : line}
            </Box>
        </Stack>
    );

    return tooltip ? <Tooltip title={tooltip} placement={'left'} arrow>{row}</Tooltip> : row;
};

/**
 * The Runs panel: history only. It never selects or moves a node and never shares a box with Preview ("would")
 * or the test-run strip. Always rendered once loaded, so it never jumps in later.
 * No failure counter: a failed last run says so in its own row (AUTOMATIONS_UI.md, Decisions).
 */
const FlowActivityPanel: React.FC<FlowActivityPanelProps> = ({
    flow,
    activity,
    nextRun,
    executionPath,
    executionsPath
}) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const language = i18n.resolvedLanguage ?? i18n.language;

    // The Live chip's heartbeat, copied on purpose: green here means a run is going, not that the flow is live.
    const pulse = useMemo(() => {
        const ring = theme.palette.success.main;
        return keyframes`
            0% { box-shadow: 0 0 0 0 ${alpha(ring, 0.35)}; }
            70% { box-shadow: 0 0 0 6px ${alpha(ring, 0)}; }
            100% { box-shadow: 0 0 0 0 ${alpha(ring, 0)}; }
        `;
    }, [theme]);

    const queued = activity?.queued ?? 0;
    const running = activity?.running ?? 0;
    const waiting = activity?.waiting ?? 0;

    const runningExecution = activity?.runningExecution;
    const waitingExecution = activity?.waitingExecution;
    const lastRun = activity?.lastRun;

    const nextRunAt = nextRun?.at;
    const isLive = flow?.status === AutomationStatus.ACTIVE;
    // Read off the run, not the flow: a paused flow's waits are real, a header test on a live flow is a test.
    const runningIsTest = isTestRunExecution(runningExecution);
    const waitingIsTest = isTestRunExecution(waitingExecution);
    // Shown before Go live too ("once live").
    const showNextRun = !!flow && !!nextRun && !!nextRunAt;

    const runningStep = runningExecution?.executionSteps?.length
        ? runningExecution.executionSteps[runningExecution.executionSteps.length - 1]?.nodeName
        : undefined;

    const resumesAt = toDate(waitingExecution?.waitUntil);

    const lastRunAt = toDate(lastRun?.startedAt ?? lastRun?.insertDate);
    const lastRunIsShown = !!lastRun?.id
        && (lastRun.id === runningExecution?.id || lastRun.id === waitingExecution?.id);
    const showLastRun = !!lastRun?.id && !!lastRunAt && !lastRunIsShown;

    if (!activity) return null;
    const nothing = !showNextRun && !queued && !running && !waiting && !showLastRun;
    const lastRunFailed = lastRun?.status === ExecutionStatus.FAILED;
    // Stopped by a Continue if: the row says where instead of "Completed", and it is no failure (no error colour).
    const lastRunStatus = stoppedAtText(lastRun, t)
        ?? (lastRun?.status ? t(EXECUTION_STATUS_LABEL_KEYS[lastRun.status] ?? lastRun.status) : '');
    const lastRunDetail = [
        lastRunAt ? formatDateRelative(lastRunAt, language) : '',
        lastRunStatus
    ].filter(Boolean).join(' · ');

    return (
        <Panel position={'top-right'}>
            <Paper
                variant={'outlined'}
                sx={{
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    borderColor: 'divider',
                    minWidth: 260,
                    maxWidth: 340,
                    px: 1.5,
                    py: 1.25
                }}
            >
                <Stack spacing={0.75}>
                    <Stack direction={'row'} alignItems={'baseline'} justifyContent={'space-between'} gap={1}>
                        <Typography variant={'body1'} fontWeight={600}>
                            {t('Runs')}
                        </Typography>
                        <Link component={RouterLink} to={executionsPath} underline={'hover'} variant={'body1'}>
                            {t('View all')}
                        </Link>
                    </Stack>

                    {nothing && (
                        <Typography variant={'body1'} color={'text.secondary'}>
                            {t('No runs yet')}
                        </Typography>
                    )}

                    {showNextRun && (
                        <ActivityRow
                            icon={<ScheduleOutlinedIcon/>}
                            text={t('Next run')}
                            detail={!isLive
                                // A slot already due fires the moment the flow goes live.
                                ? nextRun!.due
                                    ? t('as soon as it goes live')
                                    : t('{{when}}, once live', {when: formatDateRelative(nextRunAt, language)})
                                : nextRun!.due
                                    ? t('any moment now')
                                    : formatDateRelative(nextRunAt, language)}
                            tooltip={formatDateDMYHM(nextRunAt)}
                        />
                    )}

                    {queued > 0 && (
                        <ActivityRow
                            icon={<HourglassEmptyOutlinedIcon/>}
                            text={t('Flow activity queued', {count: queued})}
                            to={executionsPath}
                        />
                    )}

                    {/* A test run says so, with a still orange dot: the green heartbeat means a real run is going. */}
                    {running > 0 && (
                        <ActivityRow
                            icon={(
                                <Box
                                    component={'span'}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        m: '4px',
                                        borderRadius: '50%',
                                        bgcolor: runningIsTest ? 'warning.main' : 'success.main',
                                        flex: '0 0 auto',
                                        ...(!runningIsTest && {
                                            animation: `${pulse} 2s ease-in-out infinite`,
                                            '@media (prefers-reduced-motion: reduce)': {animation: 'none'}
                                        })
                                    }}
                                />
                            )}
                            text={runningIsTest
                                ? t('Flow activity test running', {count: running})
                                : t('Flow activity running', {count: running})}
                            detail={runningStep ? t('at {{step}}', {step: runningStep}) : undefined}
                            to={runningExecution?.id ? executionPath(runningExecution.id) : undefined}
                        />
                    )}

                    {waiting > 0 && (
                        <ActivityRow
                            icon={<PauseCircleOutlineIcon/>}
                            text={waitingIsTest
                                ? t('Flow activity test waiting', {count: waiting})
                                : t('Flow activity waiting', {count: waiting})}
                            detail={resumesAt
                                ? t('resumes {{when}}', {when: formatDateRelative(resumesAt, language)})
                                : undefined}
                            tooltip={resumesAt ? formatDateDMYHM(resumesAt) : undefined}
                            to={waitingExecution?.id ? executionPath(waitingExecution.id) : undefined}
                        />
                    )}

                    {showLastRun && (
                        <ActivityRow
                            // Not the play icon: that is the Test run button's, right above the panel.
                            icon={<HistoryRoundedIcon/>}
                            text={isTestRunExecution(lastRun) ? t('Last test run') : t('Last run')}
                            detail={lastRunDetail || undefined}
                            tooltip={formatDateDMYHM(lastRunAt)}
                            to={lastRun?.id ? executionPath(lastRun.id) : undefined}
                            error={lastRunFailed}
                        />
                    )}
                </Stack>
            </Paper>
        </Panel>
    );
};

export default FlowActivityPanel;
